/**
 * TB Cart Drawer — Upsell add-to-cart handling.
 *
 * Everything else in the drawer (open/close, quantity, remove, discount code,
 * checkout) is handled by the theme's existing global custom elements
 * (Drawer / CartDrawerComponent / DrawerCart / QuantitySelector / DiscountComponent
 * in base.js + discount.js). This file only wires up the new "Upsell" cards,
 * reusing the same _EVENT_HELPER / HELPER_UTIL globals so the drawer + progress
 * bar re-render through the exact same code path as a normal add-to-cart.
 */
(function () {
    /**
     * The free-gift add/remove itself is handled by an external mechanism
     * (app / automatic discount), not by this theme -- per the original spec
     * this drawer only has to *display* the gift line item correctly, not
     * add/remove it. That external mechanism only appears to react on a full
     * page load, not on the drawer's own AJAX cart updates -- so after
     * removing an item drops the cart below the gift threshold, the gift
     * line item can keep sitting in cart.items (and therefore in the drawer)
     * until an actual page refresh happens. A plain AJAX re-fetch of the
     * section can't fix that, since the stale line item is still genuinely
     * part of cart.items at that point. We check for exactly that situation
     * (gift still shown, but total now under threshold) and, only then,
     * force a full reload -- the one thing confirmed to make the external
     * mechanism run. Any other cart change just gets the theme's normal
     * light AJAX refresh (updateAndShowCart -> fetchAndRenderCartItems).
     */
    let correctionTimer = null;
    function reconcileGiftState() {
        const cartDrawer = document.querySelector('cart-drawer-component[data-tb-gift-threshold-cents]');
        const thresholdCents = cartDrawer
            ? parseInt(cartDrawer.getAttribute('data-tb-gift-threshold-cents'), 10)
            : 0;
        const hasGiftItem = !!document.querySelector('.tb-cart-drawer__item--gift');

        if (!cartDrawer || !thresholdCents || !hasGiftItem) {
            HELPER_UTIL.dispatchCustomEvent(_EVENT_HELPER.updateAndShowCart, document, {});
            return;
        }

        HELPER_UTIL.fetchData({
            method: 'GET',
            url: `${window.Shopify.routes.root}cart.js`,
            onsuccess: (cart) => {
                const stillHasGift = !!document.querySelector('.tb-cart-drawer__item--gift');
                if (stillHasGift && cart.total_price < thresholdCents) {
                    window.location.reload();
                } else {
                    HELPER_UTIL.dispatchCustomEvent(_EVENT_HELPER.updateAndShowCart, document, {});
                }
            },
            onerror: () => {
                HELPER_UTIL.dispatchCustomEvent(_EVENT_HELPER.updateAndShowCart, document, {});
            },
        });
    }
    function scheduleCorrectionRefresh() {
        clearTimeout(correctionTimer);
        correctionTimer = setTimeout(reconcileGiftState, 1200);
    }
    document.addEventListener(_EVENT_HELPER.OnLineItemUpdate, scheduleCorrectionRefresh);

    function handleUpsellAddClick(button) {
        if (button.disabled) return;
        const variantId = button.getAttribute('data-variant-id');
        if (!variantId) return;

        const originalLabel = button.textContent;
        button.disabled = true;
        button.textContent = '…';

        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', '1');
        formData.append(
            'sections',
            HELPER_UTIL.sectionRenderUtil('cart-drawer').getSectionsString()
        );
        // No sections_url: it's only used by Shopify to build a response for a
        // *product* page context, which we don't use (we read data.sections
        // directly) -- and Shopify rejects some values here (e.g. "/" on the
        // homepage) with "sections_url must be a relative path", which broke
        // this add-to-cart request entirely whenever the drawer was opened
        // from a non-product page.

        HELPER_UTIL.fetchData({
            method: 'POST',
            config: {
                headers: {
                    Accept: 'application/javascript',
                    'X-Requested-With': 'XMLHttpRequest',
                },
            },
            url: `${window.Shopify.routes.root}cart/add.js`,
            data: formData,
            onsuccess: (data) => {
                HELPER_UTIL.dispatchCustomEvent(
                    _EVENT_HELPER.triggerCartChange,
                    button,
                    { sections: data.sections }
                );
                scheduleCorrectionRefresh();
            },
            onerror: (error) => {
                button.disabled = false;
                button.textContent = originalLabel;
                if (error.json) {
                    error.json().then((errorObj) => {
                        HELPER_UTIL.dispatchCustomEvent(
                            _EVENT_HELPER.triggerToastMessage,
                            document,
                            {
                                toastType: 'common-errors',
                                messageType: 'error',
                                message:
                                    errorObj.message ||
                                    errorObj.description ||
                                    'Konnte nicht hinzugefügt werden.',
                            }
                        );
                    });
                }
            },
        });
    }

    document.addEventListener('click', (e) => {
        const button = e.target.closest('[data-tb-upsell-add]');
        if (!button) return;
        e.preventDefault();
        handleUpsellAddClick(button);
    });

    /**
     * The isolated Section-Rendering-API endpoint (?sections=cart-drawer,
     * used for every AJAX cart update -- quantity change, remove, add from
     * any page, the upsell "add" button above) always renders the four
     * upsell-product settings from their *schema defaults*, never from the
     * value actually configured in the Theme Editor -- confirmed with a
     * controlled test: a genuine full page load correctly reflects freshly
     * saved products, the AJAX endpoint does not, even moments after saving
     * and even after forcing a cache reset on the section file. A real page
     * load is the only path proven to read the live setting, so after every
     * cart change we fetch the current page in the background (not a real
     * navigation) and splice its freshly-correct upsells markup into the
     * open drawer, overriding whatever the AJAX section render produced for
     * that part.
     */
    let upsellRefreshTimer = null;
    function refreshUpsellsFromFullPage() {
        fetch(window.location.href, { credentials: 'same-origin' })
            .then((res) => res.text())
            .then((html) => {
                const doc = new DOMParser().parseFromString(html, 'text/html');
                const freshUpsells = doc.querySelector('.tb-cart-drawer__upsells');
                const actionEl = document.querySelector('.tb-cart-drawer__action');
                const currentUpsells = document.querySelector('.tb-cart-drawer__upsells');

                if (!actionEl) return;
                if (currentUpsells) currentUpsells.remove();
                if (freshUpsells) {
                    actionEl.insertAdjacentHTML('beforebegin', freshUpsells.outerHTML);
                }
            })
            .catch(() => {});
    }
    function scheduleUpsellsRefresh() {
        clearTimeout(upsellRefreshTimer);
        upsellRefreshTimer = setTimeout(refreshUpsellsFromFullPage, 300);
    }
    document.addEventListener(_EVENT_HELPER.triggerCartChange, scheduleUpsellsRefresh);
})();
